/* eslint-disable no-useless-escape */
/* eslint-disable import/no-webpack-loader-syntax */
import React from 'react'
import { Code } from 'rebass'

import Command from '../components/Command'
import Heading from '../components/Heading'
import P from '../components/P'
import SH from '../components/SH'
import Theory from '../components/Theory'
import Toc from '../components/Toc'
import ButtonLink from '../components/ButtonLink'
import Lost from '../components/Lost'

const IndexPage = () => (
  <div>
    <Toc active="meta" />
    <Heading fontSize={7}>Meta tags</Heading>
    <Theory>
      <P>Let's talk about providing meta tags.</P>
    </Theory>
    <Heading fontSize={5}>Titles</Heading>
    <P>
      We can set titles and other meta tags in React using React Helmet. Let's
      install it:
    </P>
    <Command>npm i react-helmet</Command>
    <P>
      We can use it to set the titles of the pages dynamically. Edit{' '}
      <Code>assets/js/MovieList.js</Code> and add an import and change its
      render method:
    </P>
    <SH
      language="jsx"
      code={`
import Helmet from "react-helmet";

//...

render() {
  if (!this.state.movies) {
    return "loading...";
  }

  return (
    <div>
      <Helmet>
        <title>Bond movies</title>
      </Helmet>
      {this.state.movies.map(movie => (
        <Link to={\`movie/\${movie.slug}\`} key={movie.slug}>
          <MovieComponent movie={movie} />
        </Link>
      ))}
    </div>
  );
}
    `}
    />
    <P>
      And now <Code>assets/js/MovieDetail.js</Code>:
    </P>
    <SH
      language="jsx"
      code={`
import Helmet from "react-helmet";

//...

render() {
  if (!this.state.movie) {
    return "Loading...";
  }
  return (
    <div>
      <Helmet>
        <title>{this.state.movie.name}</title>
      </Helmet>
      <Link to="/">Back to list</Link>
      <MovieComponent movie={this.state.movie} />
    </div>
  );
}
    `}
    />
    <P>
      This works in the browser (you should see the link in your browser), but
      if you open the source code of the page, the title is not there, so this
      won't work with crawlers that only read HTML, such as some of the Google
      crawlers and the Facebook crawler for <Code>OG:</Code> tags.
    </P>
    <P>
      Let's provide a solution for SSR in <Code>app/js/MoviesApp.js</Code>:
    </P>
    <SH
      language="jsx"
      code={`
import Helmet from "react-helmet";
import { renderToString } from "react-dom/server";

//...

export default (initialProps, context) => {
  if (context.serverSide) {
    return {
      renderedHtml: {
        componentHtml: renderToString(
          <StaticRouter
            basename={context.base}
            location={context.location}
            context={{}}
          >
            <MainApp {...initialProps} />
          </StaticRouter>
        ),
        title: Helmet.renderStatic().title.toString()
      }
    };
  } else {
    return (
      <BrowserRouter basename={context.base}>
        <MainApp {...initialProps} />
      </BrowserRouter>
    );
  }
};
    `}
    />
    <P>
      And now change <Code>templates/movies/detail.html.twig</Code> and{' '}
      <Code>templates/movies/list.html.twig</Code>
    </P>
    <SH
      language="twig"
      code={`
{% extends 'base.html.twig' %}

{% set movie = react_component_array('MoviesApp', {'props': props}) %}
{% block title %}
{{ movie.title is defined ? movie.title | raw : '' }}
{% endblock title %}

{% block body %}
{{ movie.componentHtml | raw }}
{% endblock %}`}
    />
    <P>
      If you want to unify them and make the controllers render the same
      template (since they are identical, you are more than welcome).
    </P>
    <Lost tag="05-meta" />
    <ButtonLink to="/speed" title="Go to the next section" />
  </div>
)

export default IndexPage



// WEBPACK FOOTER //
// ./src/pages/meta.js